import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { ArrowUpRight, Zap, AlertTriangle, CheckCircle2, Flame, Crown, Award } from "lucide-react";

const IMPACT_COLOR = { high: "var(--primary)", medium: "var(--warning)", low: "var(--text-muted)" };

const FACE_KEYS = ["jawline", "skin", "eyes", "symmetry", "harmony", "hairline"];
const BODY_KEYS = ["frame", "body_composition", "posture", "sex_appeal", "style", "halo"];
const ELITE_EXTRA_KEYS = [
  "canthal_tilt", "gonial_angle", "cheekbone_projection", "midface_ratio",
  "philtrum", "chin_projection", "eye_spacing", "nasal_harmony",
  "lip_ratio", "hunter_eyes_score", "masculinity", "dimorphism",
];

export default function ScanResult() {
  const { id } = useParams();
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/scan/${id}`).then((r) => setScan(r.data)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-10 text-center font-mono text-sm text-[var(--text-muted)]">LOADING...</div>;
  if (!scan) return <div className="p-10 text-center font-mono text-sm">Not found</div>;

  const a = scan.analysis || {};
  const mode = scan.mode || a.mode || "face";
  const isFull = mode === "full" || mode === "elite";
  const isElite = mode === "elite";
  const subScores = a.sub_scores || {};
  const faceData = FACE_KEYS.filter((k) => k in subScores).map((k) => ({ metric: k.toUpperCase(), score: subScores[k] }));
  const bodyData = BODY_KEYS.filter((k) => k in subScores).map((k) => ({ metric: k.replace("_", " ").toUpperCase(), score: subScores[k] }));
  const eliteData = isElite
    ? ELITE_EXTRA_KEYS.filter((k) => k in subScores).map((k) => ({ metric: k.replace(/_/g, " ").toUpperCase(), score: subScores[k] }))
    : [];
  const radarData = isElite ? [...faceData, ...bodyData, ...eliteData] : isFull ? [...faceData, ...bodyData] : faceData;

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="grid grid-cols-12 gap-6 mb-8">
        <div className="col-span-12 lg:col-span-5">
          <div className="panel-bright aspect-square overflow-hidden relative">
            <img
              src={`data:image/jpeg;base64,${scan.image_base64}`}
              alt="scan"
              className="w-full h-full object-cover"
              data-testid="result-image"
            />
            <div className="absolute top-0 left-0 right-0 p-4 flex justify-between text-[10px] font-mono uppercase tracking-widest bg-gradient-to-b from-black/80 to-transparent">
              <span className="text-white">SUBJECT // {scan.id.slice(0, 6).toUpperCase()}</span>
              <span className="text-[var(--primary)]">
                ● {isElite ? "ELITE SCAN" : isFull ? "FULL SCAN" : "FACE SCAN"}
              </span>
            </div>
          </div>
          <Link to="/scan" className="btn-ghost w-full text-center block mt-4" data-testid="rescan-link">
            Run another scan →
          </Link>
        </div>

        <div className="col-span-12 lg:col-span-7 flex flex-col">
          <span className={`tape-label ${isElite ? "flex items-center gap-1.5" : ""}`}>
            {isElite && <Crown className="w-3 h-3" />}
            {isElite ? "ELITE DIAGNOSTIC" : isFull ? "FULL DIAGNOSTIC" : "FACE DIAGNOSTIC"}
          </span>
          <div className="flex items-end gap-6 mt-4">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">OVERALL MOG SCORE</div>
              <div className="font-display text-[10rem] leading-none text-white" data-testid="overall-score">
                {scan.overall_score}
              </div>
            </div>
            <div className="pb-6">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">TIER</div>
              <div className="font-display text-4xl text-[var(--primary)]" data-testid="tier">{a.tier || "—"}</div>
            </div>
          </div>

          {isFull && a.estimates && (
            <div className="mt-6 grid grid-cols-3 gap-px bg-[var(--border)]">
              <EstTile label="BODY FAT" value={a.estimates.body_fat_pct ?? "—"} />
              <EstTile label="FRAME" value={a.estimates.frame_class ?? "—"} />
              <EstTile label="MASCULINITY" value={`${a.estimates.masculinity_index ?? 0}`} accent />
            </div>
          )}

          {isElite && a.measurements && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border)]" data-testid="elite-measurements">
              <EstTile label="CANTHAL TILT" value={a.measurements.canthal_tilt_deg ?? "—"} accent />
              <EstTile label="GONIAL ANGLE" value={a.measurements.gonial_angle_deg ?? "—"} />
              <EstTile label="MIDFACE" value={a.measurements.midface_ratio ?? "—"} />
              <EstTile label="PHILTRUM" value={a.measurements.philtrum_length ?? "—"} />
              <EstTile label="EYES" value={a.measurements.eye_shape ?? "—"} />
              <EstTile label="BODY FAT" value={a.measurements.body_fat_pct ?? "—"} />
              <EstTile label="TARGET BF" value={a.measurements.ideal_body_fat_pct ?? "—"} accent />
              <EstTile label="TARGET WT" value={a.measurements.ideal_weight_kg ?? "—"} />
            </div>
          )}

          {isElite && a.mog_match && (
            <div className="mt-4 panel-bright p-4 border-l-2 border-[var(--primary)]" data-testid="elite-mog-match">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-4 h-4 text-[var(--primary)]" />
                <span className="tape-label">MOG MATCH</span>
              </div>
              <div className="text-lg font-mono text-white">{a.mog_match.primary}</div>
              <div className="text-xs font-mono text-[var(--text-muted)]">
                {a.mog_match.secondary} · Archetype: <span className="text-[var(--primary)]">{a.mog_match.archetype}</span>
              </div>
            </div>
          )}

          <p className="mt-6 text-base text-[var(--text-muted)] leading-relaxed border-l-2 border-[var(--primary)] pl-4" data-testid="verdict">
            {a.verdict}
          </p>
        </div>
      </div>

      {/* Sub-scores radar + bars */}
      <div className="grid grid-cols-12 gap-6 mb-8">
        <div className="col-span-12 lg:col-span-6 panel p-6">
          <span className="tape-label">{isFull ? "12-METRIC RADAR" : "SUB-METRIC RADAR"}</span>
          <div className="h-96 mt-6">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} outerRadius="72%">
                <PolarGrid stroke="#2f2f2f" />
                <PolarAngleAxis dataKey="metric" tick={{ fill: "#888", fontSize: 9, fontFamily: "JetBrains Mono" }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} stroke="#2f2f2f" />
                <Radar name="Score" dataKey="score" stroke="#ff2d2d" fill="#ff2d2d" fillOpacity={0.35} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-6 panel p-6">
          <span className="tape-label">SUB-SCORES</span>
          <div className="mt-6 space-y-3">
            {Object.entries(subScores).map(([k, v]) => (
              <div key={k}>
                <div className="flex justify-between text-xs font-mono mb-1.5">
                  <span className="uppercase tracking-widest text-[var(--text-muted)]">{k.replace("_", " ")}</span>
                  <span className="text-white">{v}/100</span>
                </div>
                <div className="h-1 bg-[var(--border)] relative">
                  <div
                    className="h-full bg-[var(--primary)] transition-all"
                    style={{ width: `${v}%` }}
                    data-testid={`subscore-${k}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Strengths & weaknesses */}
      <div className="grid grid-cols-12 gap-6 mb-8">
        <div className="col-span-12 md:col-span-6 panel p-6">
          <div className="flex items-center gap-3 mb-6">
            <CheckCircle2 className="w-5 h-5 text-[var(--success)]" />
            <span className="tape-label" style={{ background: "var(--success)", color: "black" }}>STRENGTHS</span>
          </div>
          <ul className="space-y-3 text-sm">
            {(a.strengths || []).map((s, i) => (
              <li key={i} className="flex gap-3 border-l border-[var(--success)] pl-3 text-[var(--text)]">
                <span className="font-mono text-[var(--success)] text-xs">+</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="col-span-12 md:col-span-6 panel p-6">
          <div className="flex items-center gap-3 mb-6">
            <AlertTriangle className="w-5 h-5 text-[var(--primary)]" />
            <span className="tape-label">WEAKNESSES</span>
          </div>
          <ul className="space-y-3 text-sm">
            {(a.weaknesses || []).map((s, i) => (
              <li key={i} className="flex gap-3 border-l border-[var(--primary)] pl-3 text-[var(--text)]">
                <span className="font-mono text-[var(--primary)] text-xs">−</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Improvements */}
      <div className="panel p-6">
        <div className="flex items-center gap-3 mb-6">
          <Zap className="w-5 h-5 text-[var(--primary)]" />
          <span className="tape-label">IMPROVEMENT STACK</span>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] ml-2">RANKED BY IMPACT</span>
        </div>
        <div className="space-y-px bg-[var(--border)]">
          {(a.improvements || []).map((imp, i) => (
            <div key={i} className="bg-[var(--surface)] p-4 grid grid-cols-12 gap-4 items-center hover:bg-[var(--surface-2)] transition-colors" data-testid={`improvement-${i}`}>
              <div className="col-span-1 font-display text-3xl text-[var(--primary)]">0{i + 1}</div>
              <div className="col-span-3 text-xs font-mono uppercase tracking-widest text-white">{imp.area}</div>
              <div className="col-span-6 text-sm text-[var(--text-muted)]">{imp.action}</div>
              <div className="col-span-2 text-right">
                <span
                  className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border inline-flex items-center gap-1"
                  style={{ color: IMPACT_COLOR[imp.impact] || "white", borderColor: IMPACT_COLOR[imp.impact] || "white" }}
                >
                  {imp.impact === "high" && <Flame className="w-3 h-3" />}
                  {imp.impact} impact
                </span>
              </div>
            </div>
          ))}
        </div>
        <Link to={`/scan/${id}/plan`} className="btn-brutal inline-flex items-center gap-2 mt-6 mr-3" data-testid="result-to-plan">
          <Zap className="w-4 h-4" /> Generate 30-day plan
        </Link>
        <Link to="/coach" className="btn-ghost mt-6 inline-flex items-center gap-2 mr-3" data-testid="result-to-coach">
          Ask Coach <ArrowUpRight className="w-4 h-4" />
        </Link>
        <Link to="/guides" className="btn-ghost mt-6 inline-flex items-center gap-2" data-testid="result-to-guides">
          See ascending guides <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

const EstTile = ({ label, value, accent }) => (
  <div className="bg-[var(--bg)] p-3">
    <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{label}</div>
    <div className={`font-display text-2xl mt-1 ${accent ? "text-[var(--primary)]" : "text-white"}`}>{value}</div>
  </div>
);
